import { forwardRef, SelectProps } from '@chakra-ui/react';
import {
    ChakraStylesConfig,
    GroupBase,
    Props,
    Select,
    SelectInstance,
} from 'chakra-react-select';
import { SizeProp, Variant } from 'chakra-react-select/dist/types/types';
import isEqual from 'lodash.isequal';

type Option<V> = {
    value: V;
    label: string;
};

type GroupOption<V> = {
    label: string;
    options: Option<V>[];
};

export type SelectInputOption<V = any> = Option<V> | GroupOption<V>;

export interface SelectInputProps<V = any>
    extends Omit<
        Props<SelectInputOption<V>>,
        'onChange' | 'value' | 'size' | 'variant'
    > {
    components?: Props<SelectInputOption<V>>['components'];
    options: SelectInputOption<V>[];
    onChange?: (value: V) => void;
    value?: V;
    isReadOnly?: boolean;
    isSearchable?: boolean;
    tabIndex?: number;
    placeholder?: string;
    name?: string;
    size?: SelectProps['size'];
    variant?: SelectProps['variant'];
    // Style props
    width?: number;
    minWidth?: number;
    flexGrow?: number;
    h?: SelectProps['h'];
    bg?: SelectProps['bg'];
    rounded?: SelectProps['rounded'];
    border?: SelectProps['border'];
    fontSize?: SelectProps['fontSize'];
    fontWeight?: SelectProps['fontWeight'];
}

function SelectInputInner<V = any>(
    {
        value,
        onChange,
        options,
        isReadOnly,
        tabIndex,
        placeholder,
        name,
        size = 'lgr',
        variant,
        isSearchable = true,
        components,
        autoFocus,
        ...styleProps
    }: SelectInputProps<V>,
    ref: React.ForwardedRef<
        SelectInstance<
            SelectInputOption<V>,
            boolean,
            GroupBase<SelectInputOption<V>>
        >
    >
) {
    function makeChakraStyles<V = any>(): ChakraStylesConfig<
        SelectInputOption<V>
    > {
        return {
            container: (provided) => ({
                ...provided,
                ...styleProps,
                mt: 0,
            }),
            control: (provided) => ({
                ...provided,
                fontSize: size,
            }),
            option: (provided) => ({
                ...provided,
                py: 2,
                px: 3,
                minHeight: 12,
            }),
            menuList: (provided) => ({
                ...provided,
                padding: 0,
            }),
            placeholder: (provided) => ({
                ...provided,
                color: 'gray.300',
            }),
            downChevron: (provided) => ({
                ...provided,
                fontSize: 'lgr',
            }),
        };
    }

    /**
     * If the options are GroupOptions then iterate through each array of group options to find the default value.
     * Otherwise, just find the default value in the single dimensional array of options.
     * `arr.splice(1)` breaks the reduce early once the default value has been found.
     */
    const defaultValue = [...options].reduce((acc, cur: any, _, arr) => {
        const options = (cur as GroupOption<V>).options;
        if (options?.length > 0) {
            const option = options.find((o) => isEqual(o.value, value));
            if (option) {
                arr.splice(1);
                return option;
            }
        }
        if (isEqual((cur as Option<V>).value, value)) {
            arr.splice(1);
            return cur;
        }
        return acc;
    }, undefined);

    const onChangeOption = (option: any) => {
        onChange?.(option?.value);
    };

    return (
        <Select
            ref={ref}
            components={components}
            tabIndex={tabIndex}
            placeholder={placeholder || ''}
            name={name}
            isSearchable={isSearchable}
            // These are gross, but ReactSelect's typing doesn't flexibly
            // incorporate our custome themes. In practice, passing the gamut
            // of SelectProps' size & variant possibilities works fine.
            size={size as SizeProp}
            variant={variant as Variant}
            noOptionsMessage={() => null}
            chakraStyles={makeChakraStyles<V>()}
            options={options}
            value={defaultValue}
            onChange={onChangeOption}
            menuIsOpen={isReadOnly ? false : undefined}
            menuPortalTarget={document.body}
            menuShouldScrollIntoView={false}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            autoFocus={autoFocus}
        />
    );
}

const SelectInput = forwardRef<SelectInputProps, 'select'>(SelectInputInner);

export default SelectInput;
